/**
 * GlobalView — GPSJam Client (§4.4 EW/Signal Layer)
 *
 * Fetches GPS interference data from gpsjam.org.
 * Data is published as daily CSV files using H3 hexagonal grid (resolution 4).
 *
 * Source format:
 *   manifest.csv → list of available dates
 *   YYYY-MM-DD-h3_4.csv → hex,count_good_aircraft,count_bad_aircraft
 *
 * Interference levels:
 *   Green  → < 2% bad   (no interference)
 *   Yellow → 2–10% bad  (moderate)
 *   Red    → > 10% bad  (significant)
 */

import type { Entity } from '@globalview/shared';

// ─── Config ──────────────────────────────────────────────────────

/**
 * In development, Vite proxies /gpsjam-data → gpsjam.org/data
 * to bypass CORS. In production, the API backend handles this.
 */
const GPSJAM_BASE = '/gpsjam-data';
const MANIFEST_URL = `${GPSJAM_BASE}/manifest.csv`;

/** Minimum total aircraft count to consider a cell meaningful */
const MIN_AIRCRAFT_COUNT = 1;

// ─── Types ───────────────────────────────────────────────────────

export type JammingLevel = 'green' | 'yellow' | 'red';

export interface JammingCell {
  h3Index: string;
  countGood: number;
  countBad: number;
  total: number;
  percentage: number;
  level: JammingLevel;
}

// ─── Module State ────────────────────────────────────────────────

/** Cache: date string → parsed cells */
const cache = new Map<string, JammingCell[]>();
let latestDate: string | null = null;

// ─── Helpers ─────────────────────────────────────────────────────

function classifyLevel(percentage: number): JammingLevel {
  if (percentage > 10) return 'red';
  if (percentage > 2) return 'yellow';
  return 'green';
}

function parseCSV(csv: string): JammingCell[] {
  const cells: JammingCell[] = [];
  const lines = csv.split('\n');

  // Skip header line
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const parts = line.split(',');
    if (parts.length < 3) continue;

    const h3Index = parts[0];
    const countGood = parseInt(parts[1], 10);
    const countBad = parseInt(parts[2], 10);

    if (isNaN(countGood) || isNaN(countBad)) continue;

    const total = countGood + countBad;
    if (total < MIN_AIRCRAFT_COUNT) continue;

    const percentage = (countBad / total) * 100;
    const level = classifyLevel(percentage);

    cells.push({ h3Index, countGood, countBad, total, percentage, level });
  }

  return cells;
}

// ─── Public API ──────────────────────────────────────────────────

/**
 * Fetch the manifest to find the latest available date.
 */
export async function fetchLatestDate(): Promise<string | null> {
  if (latestDate) return latestDate;

  try {
    const response = await fetch(MANIFEST_URL);
    if (!response.ok) {
      console.error(`[GPSJam] Failed to fetch manifest: ${response.status}`);
      return null;
    }

    const text = await response.text();
    const lines = text.trim().split('\n');

    // Find the last non-suspect date
    for (let i = lines.length - 1; i >= 1; i--) {
      const parts = lines[i].trim().split(',');
      if (parts.length >= 3) {
        const date = parts[0];
        const suspect = parts[1] === 'true';
        if (!suspect && date) {
          latestDate = date;
          console.log(`[GPSJam] Latest date: ${latestDate}`);
          return latestDate;
        }
      }
    }

    // Fallback: use the very last date even if suspect
    const lastLine = lines[lines.length - 1].trim().split(',');
    if (lastLine[0]) {
      latestDate = lastLine[0];
      return latestDate;
    }

    return null;
  } catch (err) {
    console.error('[GPSJam] Error fetching manifest:', err);
    return null;
  }
}

/**
 * Fetch jamming data for a specific date.
 * Returns cached result if already fetched.
 */
export async function fetchJammingData(date?: string): Promise<JammingCell[]> {
  // Resolve date
  const targetDate = date ?? (await fetchLatestDate());
  if (!targetDate) {
    console.warn('[GPSJam] No date available');
    return [];
  }

  // Check cache
  const cached = cache.get(targetDate);
  if (cached) {
    console.log(`[GPSJam] Cache hit for ${targetDate} (${cached.length} cells)`);
    return cached;
  }

  // Fetch CSV
  const url = `${GPSJAM_BASE}/${targetDate}-h3_4.csv`;
  console.log(`[GPSJam] Fetching ${url}`);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`[GPSJam] Failed to fetch data: ${response.status}`);
      return [];
    }

    const text = await response.text();
    const cells = parseCSV(text);

    // Cache result
    cache.set(targetDate, cells);
    console.log(`[GPSJam] Loaded ${cells.length} cells for ${targetDate}`);

    return cells;
  } catch (err) {
    console.error('[GPSJam] Error fetching data:', err);
    return [];
  }
}

/**
 * Convert jamming cells to the normalized Entity format for the entity store.
 */
export function jammingCellsToEntities(cells: JammingCell[], date: string): Entity[] {
  return cells.map((cell) => ({
    id: `ew-${cell.h3Index}`,
    type: 'jamming_cell' as const,
    source: 'gpsjam',
    position: { lat: 0, lon: 0, alt: 0 }, // actual position resolved from H3 in renderer
    timestamp: `${date}T00:00:00Z`,
    style: {
      color: cell.level === 'red' ? '#ff3333' : cell.level === 'yellow' ? '#ffcc00' : '#33cc33',
      size: 1,
    },
    properties: {
      h3Index: cell.h3Index,
      level: cell.level,
      countGood: cell.countGood,
      countBad: cell.countBad,
      total: cell.total,
      percentage: cell.percentage,
    },
    confidence: 1,
  }));
}

/**
 * Clear the data cache (useful when switching dates).
 */
export function clearCache(): void {
  cache.clear();
  latestDate = null;
}
